import { errorWords } from "@alm/domain";
import { createFileRoute } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { toast } from "sonner";
import { EmptyState } from "@/components/app/lists/empty-state";
import { PageHeader } from "@/components/app/shell/page-header";
import { ReadFailed } from "@/components/app/lists/read-failed";
import { Skeleton } from "@/components/ui/skeleton";
import { useFacility } from "@/lib/facility/facility";
import { orpc } from "@/lib/orpc";
import { useCan, useFeature } from "@/lib/policy";
import { readState } from "@/lib/query/read-state";
import { BulkApplyForm, type BulkApplyValues } from "./-components/bulk-apply-form";

export const Route = createFileRoute("/_authenticated/_workforce/hr/bulk-apply")({
  component: BulkApply,
});

/** What the counts mean, said the way somebody who just pressed Apply would say it. */
function describe(result: { documents: number; courses: number; tagged: number }): string {
  const parts = [
    result.documents > 0 ? `${result.documents} documents asked for` : null,
    result.courses > 0 ? `${result.courses} courses assigned` : null,
    result.tagged > 0 ? `${result.tagged} labels applied` : null,
  ].filter(Boolean);
  return parts.length === 0 ? "Everybody already had all of it" : parts.join(", ");
}

/** One action against a group of people. Every piece of this is reachable one employee at a time
 * from their own file. This is the screen for the other case: a shift's worth of new starters who
 * all need the same paperwork, the same mandatory courses and the same label. */
function BulkApply() {
  const { facilityId } = useFacility();
  const queryClient = useQueryClient();
  const canManageTraining = useCan("training:manage");
  const trainingOn = useFeature("training");
  const canAssignTraining = canManageTraining && trainingOn;
  const [applied, setApplied] = useState<string | null>(null);

  const facilities = useQuery(
    orpc.administration.facilities.list.queryOptions({ input: { includeInactive: false } }),
  );
  const employees = useQuery(
    orpc.workforce.employeeDocuments.employees.overview.queryOptions({
      input: { facilityId, includeInactive: false },
    }),
  );
  const documentTemplates = useQuery(
    orpc.workforce.employeeDocuments.listTemplates.queryOptions({ input: {} }),
  );
  const trainingTemplates = useQuery({
    ...orpc.workforce.training.listTemplates.queryOptions({}),
    enabled: canAssignTraining,
  });
  const labels = useQuery(
    orpc.administration.catalogs.tags.list.queryOptions({ input: { facilityId } }),
  );

  const apply = useMutation(
    orpc.workforce.employeeDocuments.employees.bulkApply.mutationOptions({
      onSuccess: async (result) => {
        setApplied(describe(result));
        toast.success(describe(result));
        await queryClient.invalidateQueries({ queryKey: orpc.workforce.key() });
      },
      onError: (error) => toast.error(errorWords(error)),
    }),
  );

  const buildings = facilities.data ?? [];
  // The action writes to one building, so "all" is not an answer it can take.
  const defaultFacilityId = facilityId !== "all" ? facilityId : (buildings[0]?.id ?? "");

  const header = (
    <PageHeader
      title="Apply in bulk"
      meta={applied ?? "Put a template, a course bundle or labels on a group of people at once."}
    />
  );
  /* A failed read is said as one, not as "no buildings" or an empty list of
     people: either would send somebody off to add what already exists. */
  const failed = facilities.isError
    ? {
        message: "Could not load your buildings.",
        error: facilities.error,
        retry: () => void facilities.refetch(),
        retrying: facilities.isFetching,
      }
    : employees.isError
      ? {
          message: "Could not load the employees.",
          error: employees.error,
          retry: () => void employees.refetch(),
          retrying: employees.isFetching,
        }
      : readState(labels) === "failed"
        ? {
            message: "Could not load the labels.",
            error: labels.error,
            retry: () => void labels.refetch(),
            retrying: labels.isFetching,
          }
        : null;

  if (failed) {
    return (
      <>
        {header}
        <ReadFailed
          message={failed.message}
          error={failed.error}
          onRetry={failed.retry}
          retrying={failed.retrying}
        />
      </>
    );
  }
  if (facilities.isPending || employees.isPending) {
    return (
      <>
        {header}
        <Skeleton className="h-96 w-full max-w-2xl" />
      </>
    );
  }
  if (buildings.length === 0) {
    return (
      <>
        {header}
        <EmptyState
          title="No buildings to apply anything to"
          description="Ask an administrator to add a building you can see."
        />
      </>
    );
  }

  return (
    <>
      {header}
      <BulkApplyForm
        key={defaultFacilityId}
        facilityId={defaultFacilityId}
        facilities={buildings}
        employees={(employees.data ?? []).map((person) => ({
          id: person.id,
          label: person.name || "Unnamed",
          meta: person.jobTitle,
        }))}
        documentTemplates={documentTemplates.data ?? []}
        trainingTemplates={trainingTemplates.data ?? []}
        tags={(labels.data ?? []).map((tag) => ({ id: tag.id, label: tag.name }))}
        canAssignTraining={canAssignTraining}
        pending={apply.isPending}
        onSubmit={(values: BulkApplyValues) => apply.mutate(values)}
      />
    </>
  );
}
